// config.js — Effort constants, HC weights, and calculation utilities.
// Extracted from script.js (lines 17–291).
// No imports — this module has zero dependencies.

// Shared block-hour lookup tables — referenced by name below so identical tables are
// only defined once. Use separate arrays only when rates need to diverge independently.
const _standardJobBlocks = [
    { max: 50,   hours: 4  },
    { max: 100,  hours: 8  },
    { max: 200,  hours: 12 },
    { max: 400,  hours: 20 },
    { max: 600,  hours: 28 },
    { max: 800,  hours: 36 },
    { max: 1000, hours: 44 }
];
// Repository migration design effort — flat baseline + incremental per 250 TB.
// Applies to both VBR and VB365 repo migrations; input is total data volume (TB) across all scopes.
// Included in design effort for both REA and Standard PS modes.
const _repoMigrationDesignBlocks = [
    { max: 250,  hours: 4  },
    { max: 500,  hours: 6  },
    { max: 750,  hours: 8  },
    { max: 1000, hours: 10 },
    { max: 9999, hours: 12 }
];
// Per-product design block tables — applied per workload group and summed.
// Design block tables store the VM COUNT MODIFIER only — the 4h flat base is added
// separately in recalc.js and shown via baseline: 4 in the BLOCK_TABLE_REGISTRY display.
// Total design hours per product = 4 (base) + blockHours(table, vmCount).

// VBR: VM count modifier
const _vbrDesignBlocks = [
    { max: 50,   hours: 4  },
    { max: 100,  hours: 8  },
    { max: 200,  hours: 12 },
    { max: 400,  hours: 20 },
    { max: 600,  hours: 28 },
    { max: 800,  hours: 36 },
    { max: 1000, hours: 44 }
];
// VB365: user count modifier
const _vb365DesignBlocks = [
    { max: 50,   hours: 3  },
    { max: 100,  hours: 5  },
    { max: 200,  hours: 8  },
    { max: 400,  hours: 12 },
    { max: 600,  hours: 16 },
    { max: 800,  hours: 20 },
    { max: 1000, hours: 24 }
];
// Kasten K10: namespace count modifier
const _kastenDesignBlocks = [
    { max: 10,   hours: 4  },
    { max: 25,   hours: 8  },
    { max: 50,   hours: 12 },
    { max: 100,  hours: 16 },
    { max: 200,  hours: 24 },
    { max: 500,  hours: 32 },
    { max: 1000, hours: 40 }
];
// VBforCloud: VM count modifier
const _vbfCloudDesignBlocks = [
    { max: 50,   hours: 0  },
    { max: 100,  hours: 2  },
    { max: 200,  hours: 4  },
    { max: 400,  hours: 6  },
    { max: 600,  hours: 10 },
    { max: 800,  hours: 14 },
    { max: 1000, hours: 18 }
];
// Veeam ONE: monitored-estate VM count modifier.
// 2h for the first 0–200 monitored VMs, +1h per additional 200 VMs.
const _voneDesignBlocks = [
    { max: 200,  hours: 2 },
    { max: 400,  hours: 3 },
    { max: 600,  hours: 4 },
    { max: 800,  hours: 5 },
    { max: 1000, hours: 6 }
];
// Veeam Recovery Orchestrator: orchestrated-estate VM count modifier.
// 4h for the first 0–200 orchestrated VMs, +2h per additional 200 VMs
// (heavier than VONE — recovery plan / dependency / runbook design).
const _vroDesignBlocks = [
    { max: 200,  hours: 4  },
    { max: 400,  hours: 6  },
    { max: 600,  hours: 8  },
    { max: 800,  hours: 10 },
    { max: 1000, hours: 12 }
];
const _standardReplicationBlocks = [
    { max: 100,  hours: 8  },
    { max: 200,  hours: 12 },
    { max: 400,  hours: 16 },
    { max: 600,  hours: 24 },
    { max: 800,  hours: 32 },
    { max: 1000, hours: 40 }
];

// --- Effort formula configuration ---
// Total estimate formula:
// baseline kickoff + discovery
// + design block hours when design is required
// + implementation block hours
// + skills transfer hours per selected workload
// + VONE baseline and per-workload hours when enabled
// + VONE customisation hours per selected reporting/dashboard/alarm option
// + site effort per site: 4h if the site has workloads or jobs, 1h if it is infrastructure/repo-only
// + backup verification uplift when limited verification is required
// + distributed deployment effort per proxy and per repository when distributed is enabled
// + VSA v13 uplift when VSA v13 is not enabled
// + replication block hours when replication is enabled
// + CDP replication block hours when CDP replication is enabled
// + upgrade health-check add-on hours
// + SureBackup, backup copy, SureReplica, tape, and VRO add-on hours
// + application-aware processing uplift by selected coverage
// + VDC Vault migration job, gateway, source-storage, and target-vault uplifts
// + enablement hours per selected workload when enabled
// + UAT hours per selected workload when enabled
// + as-built documentation flat hours when enabled
// + DR runbook flat hours when enabled
// + job validation block hours when upgrade or health check and job count > 0
export const effortConfig = {
    baselineHours: {
        kickoff: 2,             // Flat internal project kick-off hours for every estimate (project initiation, stakeholder alignment)
        customerKickoff: 2,     // Flat customer (external) project kick-off hours for every estimate (customer-facing project initiation meeting)
    },
    // Per-site discovery overhead — flat rate per site regardless of content.
    // Every site requires its own scoping session/call, so site 1 is not free.
    discoveryPerSite: 1,
    // Per-component discovery rates — direct hours per unit of each Veeam component type.
    // VBR requires the most discovery effort (architecture, jobs, config review);
    // proxies and repositories require less.
    // Sources: site.existingEnvironment (EE chip counts) + site.veeamServers (named instances).
    discoveryComponent: {
        vbrServer:          5,  // site.existingEnvironment.vbrServers
        vbrProxy:           0.5,  // site.existingEnvironment.proxyServersWindows + proxyServersLinux
        vbrRepository:      1,  // site.existingEnvironment.repositoryServers
        vb365Server:        3,  // site.veeamServers where type === 'vb365'
        vb365Proxy:         0.5,  // site.existingEnvironment.vb365ProxyServers
        vb365Repository:    1,  // site.existingEnvironment.vb365RepositoryServers
        vbfCloudServer:     4,  // site.veeamServers where type in ['vbfaws','vbfaz','vbfgc']
        vbfCloudRepository: 1,  // site.existingEnvironment.vbfCloudRepositoryServers
        kastenServer:       4,  // site.veeamServers where type === 'kasten'
    },
    // Workload-scale discovery block table — input is all workloads in scope (EE inventory + unprotected) + managed job VM counts.
    // Output is added to componentDiscoveryHours + discoverySiteHours to give total discoveryHours.
    discoveryBlocks: [
        { max: 50,   hours: 1  },
        { max: 150,  hours: 2  },
        { max: 400,  hours: 3  },
        { max: 750,  hours: 4  },
        { max: 1500, hours: 6  },
        { max: 2000, hours: 8  },
        { max: 5000, hours: 10 },
        { max: 9999, hours: 12 }
    ],
    enablementHours: {
        flat: 6                 // Flat rate when Skills Transfer Workshop is enabled in Step 2
    },
    uatHours: {
        perHypervisor: 6        // Formula: selected workloads * 6 when UAT is required
    },
    veeamOne: {
        baseline: 8,            // Formula: +8 when VONE is required
        perHypervisor: 4,       // Formula: selected workloads * 4 when VONE is required
        customOption: 8         // Formula: selected add-ons * 8
    },
    backupVerification: {
        instancesPerBlock: 25,  // 1 hour per 25 instances protected in backup jobs
        minHours: 1             // Minimum 1h when verification is required
    },
    repoMigrationDesignBlocks: _repoMigrationDesignBlocks, // Design effort for VBR/VB365 repo migrations (by total TB)
    designBlocksVbr:      _vbrDesignBlocks,      // Design effort for VBR-managed workloads
    designBlocksVb365:    _vb365DesignBlocks,    // Design effort for VB365-managed workloads (Microsoft 365)
    designBlocksKasten:   _kastenDesignBlocks,   // Design effort for Kasten K10 (Kubernetes namespaces)
    designBlocksVbfCloud: _vbfCloudDesignBlocks, // Design effort for VBforCloud (AWS/Azure/GCP native)
    designBlocksVone:     _voneDesignBlocks,     // Design effort for Veeam ONE (monitored-estate VM count) — 2h base added separately
    designBlocksVro:      _vroDesignBlocks,      // Design effort for Veeam Recovery Orchestrator (orchestrated-estate VM count) — 4h base added separately
    implementationBlocks: _standardJobBlocks,    // Block-based implementation effort by instance count
    expansionBlocks: [                           // Effort to expand existing job scope (delta VM count)
        { max: 25,       hours: 2  },
        { max: 100,      hours: 4  },
        { max: 250,      hours: 6  },
        { max: 500,      hours: 8  },
        { max: Infinity, hours: 12 },
    ],
    advisorySupport: { multiplier: 0.25 },  // Formula: fullImplementationHours × 0.25 when implementation advisory support is enabled (Design Only mode)
    templateJobs: { implReductionMultiplier: 0.15 }, // Formula: fullImplementationHours × 0.15 when template/skeleton jobs are used (Standard PS) — PS creates framework jobs; customer populates remaining workloads
    replicationBlocks: _standardReplicationBlocks,      // Block-based replication effort by VM count
    cdpReplicationBlocks: _standardReplicationBlocks,   // Block-based CDP replication effort by VM count
    distributedDeployment: {
        perProxy: 2,            // Formula: proxies * 2
        perRepository: 2        // Formula: repositories * 2
    },
    vsaV13: {
        disabledAdjustment: 4   // Formula: +4 when VSA v13 is not enabled
    },
    domainMetricEffort: {
        effortPerNamespace: 1,  // Formula: namespaces * 1
        usersPer1000Block: 1000,
        effortPer1000Users: 1   // Formula: ceil(users / 1000) * 1
    },
    upgradeHealthCheck: {
        lite: 8,
        full: 16,
        designHours: 2  // Formula: flat hours for HC scope definition & report template prep (Design phase)
    },
    vro: {
        baseline: 8,
        perScopedItem: 2
    },
    sureBackup: {
        flat: 8
    },
    backupCopy: {
        baseline: 4,
        blocks: [
            { max: 50, hours: 4 },
            { max: 100, hours: 6 },
            { max: 200, hours: 8 },
            { max: 400, hours: 12 },
            { max: 600, hours: 16 },
            { max: 800, hours: 20 },
            { max: 1000, hours: 24 }
        ]
    },
    sureReplica: {
        flat: 8
    },
    asBuiltDocumentation: {
        flat: 8             // Formula: +8 when As-Built documentation is required
    },
    drRunbook: {
        flat: 8             // Formula: +8 when DR runbook / recovery procedures document is required
    },
    sop: {
        flat: 8             // Formula: +8 when Standard Operating Procedures (SOPs) are required
    },
    handoverWorkshop: {
        flat: 4             // Formula: +4 flat — handover workshop always included in Handover phase
    },
    jobValidation: {
        blocks: [           // Formula: block hours by blended HC units (HC only)
            { max: 25,   hours: 2 },
            { max: 50,   hours: 4 },
            { max: 100,  hours: 8 },
            { max: 200,  hours: 12 },
            { max: 400,  hours: 16 },
            { max: 600,  hours: 20 },
            { max: 1000, hours: 24 }
        ]
    },
    upgradePreFlight: {
        blocks: [           // Formula: lighter pre-flight scaling when upgrade active but no HC (~40% of jobValidation)
            { max: 25,   hours: 1 },
            { max: 50,   hours: 2 },
            { max: 100,  hours: 3 },
            { max: 200,  hours: 5 },
            { max: 400,  hours: 7 },
            { max: 600,  hours: 8 },
            { max: 1000, hours: 10 }
        ]
    },
    upgradeComponent: {
        vbr:            8,  // Formula: VBR instances with upgrade.vbr selected × 8h
        proxy:          2,  // Formula: VBR proxy servers (inferred from VBR upgrade) × 2h
        repo:           2,  // Formula: VBR repository servers (inferred from VBR upgrade) × 2h
        vb365Proxy:     2,  // Formula: VB365 proxy servers (inferred from VB365 upgrade) × 2h
        vb365Repo:      2,  // Formula: VB365 repository servers (inferred from VB365 upgrade) × 2h
        vbfCloudRepo:   2,  // Formula: VBfCloud repository servers (inferred from VBR upgrade) × 2h
        vbfaws:         4,  // Formula: VBfAWS appliance instances with upgrade selected × 4h
        vbfaz:          4,  // Formula: VBfAz appliance instances with upgrade selected × 4h
        vbfgc:          4,  // Formula: VBfGC appliance instances with upgrade selected × 4h
        kasten:         4,  // Formula: Kasten K10 instances with upgrade selected × 4h
    },
    applicationAwareProcessing: {
        per25PercentBlock: 2
    },
    tapeIntegration: {
        sas: 8,
        'fiber-channel': 12
    },
    architectureAdjustments: {
        linux: 4,                   // Formula: +4h added to newVbrInstall hours when VBR OS is Linux
        distributedSiteTopology: 8  // Formula: +8h when siteCount > 1 and topology is distributed
    },
    newVbrInstall: {
        virtual: 4,    // Formula: +4h base for a virtual VBR deployment
        physical: 8    // Formula: +8h base for a physical VBR deployment (rack/stack, OS install)
    },
    newVbfCloudInstall: 4, // Formula: +4h per new VBfAWS/VBfAz/VBfGC appliance lane (deploy + base config)
    newVb365Install: 6,   // Formula: +6h per new VB365 server lane (deploy + connector config + base policy)
    repoMigration: {
        perJob: 2,
        perBackupCopyJob: 2,
        perGateway: 4,
        sourceStorageType: {
            'azure-blob': 2,
            'aws-s3': 2,
            'onprem-s3': 4,
            'smb-nfs': 4,
            'cloud-s3': 2,
            'onprem-block': 6
        },
        targetVault: {
            'vdc-vault-aws': 1,
            'vdc-vault-azure': 1
        },
        targetRepo: {
            // Non-vault repository migration target overhead (per unique target type per site)
            'block-performance-tier': 1,   // +1h per site for block perf tier target
            'blob-performance-tier': 2     // +2h per site for BLOB perf tier target (object storage config)
        }
    },
    hypervisorMigration: {
        decommissionPerScope: 4,    // Formula: flat hours per migration scope for job decommission and cutover
        proxyReconfig: 8,           // Formula: +8 per scope when proxy reconfiguration is required
        restoreTesting: 8           // Formula: +8 per scope when post-cutover restore testing is included
    },
    projectManagement: {
        rate: 0.10          // Formula: 10% of base effort subtotal
    },
    contingency: {
        rate: 0.10          // Formula: 10% of base effort subtotal (risk buffer)
    },
    vb365RepositoryMigration: {
        // Formula (per scope — repo count and data TB derived from Step 1):
        //   basePerScope
        //   + perRepo × vb365TotalRepoCount
        //   + versionUplift[detectedVb365Version]
        //   + blockHours(dataSizeBlocks, vb365TotalConsumedTb)
        basePerScope: 4,            // Flat assessment and planning effort per migration configuration
        perRepo: 4,                 // Per-repository migration effort (stop jobs, redirect storage, verify)
        versionUplift: {
            v8: 0,                  // Current release — no uplift
            v7: 4,                  // Previous major — some manual reconfiguration steps
            v6: 8                   // Two versions back — significant manual work, limited tooling support
        },
        dataSizeBlocks: [           // Scales with total consumed TB across all VB365 repos in Step 1
            { max: 100,  hours: 12 },
            { max: 400,  hours: 24 },
            { max: 1000, hours: 40 },
            { max: 2500, hours: 60 },
            { max: 5000, hours: 80 },
            { max: 99999, hours: 80 }
        ]
    },
    vbrRepoMigration: {
        // Formula (per scope — each scope row = 1 repository):
        //   blockHours(dataSizeBlocks, dataTb)
        //     where dataTb is summed from Step 1 workload protectedTb values
        //     matching this scope's sourceType
        basePerScope: 4,            // Retained in config but not used in formula
        hardeningSetup: 8,          // Retained in config but not used in formula
        perRepository: 3,           // Retained in config but not used in formula
        sobrComplexityAdd: 4,       // Retained in config but not used in formula
        dataSizeBlocks: [           // Data movement complexity scales with volume
            { max: 100,  hours: 12 },
            { max: 400,  hours: 24 },
            { max: 1000, hours: 40 },
            { max: 2500, hours: 60 },
            { max: 5000, hours: 80 },
            { max: 99999, hours: 80 }
        ]
    }
};
// --- End effort formula configuration ---

/**
 * NAS / unstructured-data effort weighting.
 *
 * Unstructured-data (NAS / object storage) workloads store their quantity as TB, not as
 * machine instances. Counting that TB 1:1 against VMs and physical agents massively
 * over-states design and implementation effort — 100 TB of file/object storage is far less
 * work to protect than 100 VMs. This weight converts a NAS workload entry's TB into a small,
 * VM-equivalent "effort unit" count used by the design, implementation, and discovery block
 * tables:
 *
 *     effort units (per NAS entry) = perShare + round(TB × tbPerUnit)
 *
 *   • perShare  — flat effort signal per NAS share / filer entry (the "count by shares" part)
 *   • tbPerUnit — light, volume-based component; each TB ≈ tbPerUnit of one VM's effort.
 *                 No divisor cliff — it scales smoothly with data volume.
 *
 * Lower tbPerUnit ⇒ less NAS effort. Tune these two values to calibrate NAS-heavy estimates.
 * Example: 1272 TB across 2 shares → 1 + 1 + round(1272 × 0.05) ≈ 66 units (vs 1272 before).
 */
export const nasEffortWeight = {
    perShare:  1,
    tbPerUnit: 0.05,
};

/**
 * Registry of all block-hour lookup tables used in the effort calculation.
 * Consumed by formula-debug.js to render the Block Tables panel — adding a
 * new block table here automatically makes it appear in the debug panel.
 *
 * Fields:
 *   id          — unique identifier (kebab-case)
 *   title       — card header text
 *   color       — card header background colour
 *   metric      — describes the input axis (shown as subtitle in the card header)
 *   blocks      — reference to the block array in effortConfig
 *   inputKey    — key in debugData whose value is the current input; null = per-scope (no single live value)
 *   baseline    — additive flat offset applied before the block lookup (e.g. backupCopy.baseline)
 */
export const BLOCK_TABLE_REGISTRY = [
    {
        id:     'discovery',
        title:  'Discovery',
        color:  '#2e7d32',
        metric: 'existing VMs + job VMs',
        blocks:   effortConfig.discoveryBlocks,
        inputKey: 'discoveryWorkloadCount',
    },
    {
        id:          'design-vbr',
        title:       'Design (VBR)',
        color:       '#5c4fcf',
        metric:      'effort units (unprotected VMs)',
        blocks:      effortConfig.designBlocksVbr,
        inputKey:    '_vbrDesignVms',
        baseline:    4,
    },
    {
        id:          'design-vb365',
        title:       'Design (VB365)',
        color:       '#5c4fcf',
        metric:      'effort units (unprotected VMs)',
        blocks:      effortConfig.designBlocksVb365,
        inputKey:    '_vb365DesignVms',
        baseline:    4,
    },
    {
        id:          'design-kasten',
        title:       'Design (Kasten K10)',
        color:       '#5c4fcf',
        metric:      'effort units (namespaces)',
        blocks:      effortConfig.designBlocksKasten,
        inputKey:    '_kastenDesignVms',
        baseline:    4,
    },
    {
        id:          'design-vbfcloud',
        title:       'Design (VBforCloud)',
        color:       '#5c4fcf',
        metric:      'effort units (unprotected VMs)',
        blocks:      effortConfig.designBlocksVbfCloud,
        inputKey:    '_vbfCloudDesignVms',
        baseline:    4,
    },
    {
        id:          'repo-migration-design',
        title:       'Repo Migration Design (VBR + VB365)',
        color:       '#1a7a5e',
        metric:      'total data volume (TB)',
        blocks:      effortConfig.repoMigrationDesignBlocks,
        inputKey:    'vbrRepoMigrationTotalTb',
    },
    {
        id:          'implementation',
        title:       'Implementation',
        color:       '#5c4fcf',
        metric:      'effort units (unprotected VMs)',
        blocks:      effortConfig.implementationBlocks,
        inputKey:    'effortTotalInstances',
    },
    {
        id:       'replication-cdp',
        title:    'Replication & CDP',
        color:    '#e05c2e',
        metric:   'effort units',
        blocks:   effortConfig.replicationBlocks,
        inputKey: 'effortTotalInstances',
    },
    {
        id:       'backup-copy',
        title:    'Backup Copy',
        color:    '#e05c2e',
        metric:   'effort units',
        blocks:   effortConfig.backupCopy.blocks,
        inputKey: 'effortTotalInstances',
        baseline: effortConfig.backupCopy.baseline,
    },
    {
        id:       'job-validation',
        title:    'Job Validation',
        color:    '#7a7a7a',
        metric:   'blended HC units',
        blocks:   effortConfig.jobValidation.blocks,
        inputKey: 'totalHcUnits',
    },
    {
        id:       'upgrade-preflight',
        title:    'Upgrade Pre-flight',
        color:    '#888830',
        metric:   'blended HC units',
        blocks:   effortConfig.upgradePreFlight.blocks,
        inputKey: 'totalHcUnits',
    },
    {
        id:       'vb365-repo-data',
        title:    'VB365 Repo Migration (TB)',
        color:    '#1a7a5e',
        metric:   'consumed TB',
        blocks:   effortConfig.vb365RepositoryMigration.dataSizeBlocks,
        inputKey: 'vb365TotalConsumedTb',
    },
    {
        id:       'vbr-repo-data',
        title:    'VBR Repo Migration (TB)',
        color:    '#1a7a5e',
        metric:   'data TB (per scope)',
        blocks:   effortConfig.vbrRepoMigration.dataSizeBlocks,
        inputKey: null,
    },
];

// ── HC / Upgrade effort weights ───────────────────────────────────────────────

/**
 * Workload-type complexity weight for health-check job validation.
 *   1 = Standard: hypervisors, agents, cloud, NAS/object storage
 *   2 = Enterprise plugins: M365, Kubernetes, SAP, Oracle RMAN, IBM Db2, SQL Server plugin
 * Defaults to 1 for any unlisted type.
 */
export const HC_WORKLOAD_WEIGHT = {
    // Hypervisors (all = 1)
    'vmware': 1, 'vmware-cloud-director': 1, 'hyperv': 1, 'nutanix': 1,
    'proxmox': 1, 'scale-computing-hypercore': 1, 'hpe-morpheus-vm-essentials': 1, 'kvm': 1,
    // Physical agents (all = 1)
    'agent-windows': 1, 'agent-linux-kernel': 1, 'agent-linux-nosnap': 1,
    'agent-aix': 1, 'agent-solaris': 1, 'agent-mac': 1,
    'cdp-universal-agent': 1,
    // Additional hypervisors (explicitly listed for completeness — all = 1)
    'ovirt': 1,
    // Cloud — same complexity as hypervisors (= 1)
    'entra-id': 1,
    'aws-ec2': 1, 'aws-rds': 1, 'aws-dynamodb': 1, 'aws-redshift': 1,
    'aws-redshift-serverless': 1, 'aws-efs': 1, 'aws-fsx': 1, 'aws-vpc': 1,
    'azure-vm': 1, 'azure-sql': 1, 'azure-cosmosdb': 1, 'azure-files': 1, 'azure-vnet': 1,
    'gcp-vm': 1, 'gcp-sql': 1, 'gcp-spanner': 1,
    // Unstructured / object storage (= 1)
    'unstructured-data': 1,
    // Enterprise plugins — more specialised review process (= 2)
    'veeam-backup-microsoft-365': 2,
    'kasten-k10': 2,
    'plugin-sql-server': 2,
    'plugin-oracle-rman': 2,
    'plugin-sap-oracle': 2,
    'plugin-sap-hana': 2,
    'plugin-sap-maxdb': 2,
    'plugin-ibm-db2': 2,
};

/**
 * Per-job-type factor for HC job validation.
 *   ×1   — Backup, Backup Copy, Replication (straightforward job review)
 *   ×1.5 — SureBackup (virtual lab), SureReplica (failover-plan verification)
 *   ×2   — CDP Replication (storage-level integration, sub-second RPO review)
 */
export const HC_JOB_TYPE_FACTOR = {
    'backup':          1,
    'backup-copy':     1,
    'replication':     1,   // bumps to 1.5 when SureReplica is also required (see calculation)
    'surebackup':      1.5,
    'cdp-replication': 2,
};

/** Returns the blended HC effort units for a single managed canvas job. */
export function calcJobHcUnits(job, sureReplicaActive) {
    const workloadWeight = HC_WORKLOAD_WEIGHT[job.workloadType] ?? 1;
    let jobFactor = HC_JOB_TYPE_FACTOR[job.jobType] ?? 1;
    // Replication jobs gain a SureReplica complexity uplift when SureReplica is required
    if (job.jobType === 'replication' && sureReplicaActive) jobFactor = 1.5;
    const count = Math.max(0, parseInt(job.count, 10) || 0);
    return Math.ceil(workloadWeight * jobFactor * (1 + Math.floor(count / 50)));
}

// ── End HC / Upgrade effort weights ──────────────────────────────────────────

/**
 * EE component fields whose quantity is locked (driven by veeamServers on the canvas)
 * rather than entered manually. Defined here — zero deps — so both recalc.js and
 * greenfield-sortables.js can import it without introducing a circular dependency.
 */
export const lockedQtyFieldKeys = new Set(['vbrServers', 'vroServers', 'veeamOneServers', 'vb365RepositoryServers']);

/**
 * Looks up hours from a block table by instance count.
 * Used by effortConfig.designBlocks, implementationBlocks, replicationBlocks, etc.
 */
export function blockHours(blocks, instanceCount) {
    const normalizedCount = Math.max(0, instanceCount);
    if (normalizedCount === 0) {
        return 0;
    }

    const count = Math.min(normalizedCount, 1000);
    const block = blocks.find(item => count <= item.max);
    return block ? block.hours : blocks[blocks.length - 1].hours;
}
